import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import {
  AlertTriangle,
  ChevronRight,
  Clock,
  Banknote,
  Loader2,
  Users,
  CalendarPlus,
  MessageSquareWarning,
  MapPin,
  Star,
  ClipboardCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isValid } from 'date-fns';
import { getShiftStatus, statusToneClasses } from '../../lib/shiftStatus';
import useGreeting from '../../hooks/useGreeting';

// Phase 4 UX (Feb 2026): the manager home is rebuilt around ONE question —
// "What do you need to do today?"  The old dashboard led with four
// decorative stat tiles (Shifts / Active Now / Urgent Alerts / Needs
// Review); those competed with the primary Find Cover action and rarely
// drove daily behaviour. They are removed here.
//
// New anatomy:
//   1. Greeting + "Need cover?" hero with a dominant Find Cover CTA.
//   2. Needs attention — only actionable items, deep-linked to the shift.
//   3. Upcoming shifts — next few cards, each with ONE primary action.
//
// The Top Performers list, Active Shifts double-grid and stat cards were
// intentionally dropped; managers still reach that data via Shifts, Alerts
// and Staff pages. All backend contracts remain unchanged.
export default function ManagerDashboard() {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, shiftsRes] = await Promise.all([
        api.get('/dashboard/manager-stats'),
        api.get('/shifts'),
      ]);
      setStats(statsRes.data);
      setShifts(shiftsRes.data);
    } catch (_e) {
      toast.error('Failed to load your home screen');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); if (refreshUser) refreshUser(); }, [fetchData]);

  const handleUpdateCard = async () => {
    setCardLoading(true);
    try {
      const res = await api.post('/payments/setup-card', { originUrl: window.location.origin });
      if (res.data?.url) { window.location.href = res.data.url; return; }
    } catch { toast.error('Could not start card setup'); }
    finally { setCardLoading(false); }
  };

  // --- Derived data ---------------------------------------------------------
  const { timeGreeting, firstName, isFirstVisit } = useGreeting(user);

  // Actionable "Needs attention" list — pulled from the same shift data so
  // we don't rely on new API surfaces. Each entry is a plain object:
  //   { key, icon, tone, message, actionLabel, onClick }
  const now = Date.now();
  const oneDayMs = 24 * 60 * 60 * 1000;

  const needsAttention = [];

  // 1. Open shifts with applicants waiting for a decision
  shifts
    .filter((s) => s.status === 'open' && (s.applicantCount || 0) > 0)
    .slice(0, 3)
    .forEach((s) => {
      needsAttention.push({
        key: `applicants-${s.id}`,
        icon: Users,
        tone: 'warning',
        message: `${s.applicantCount} worker${s.applicantCount === 1 ? '' : 's'} waiting for your decision — ${s.branchName}`,
        actionLabel: 'Review workers',
        onClick: () => navigate(`/manager/applicants?shiftId=${s.id}`),
      });
    });

  // 2. Assigned shifts happening tomorrow / very soon that still might need help
  shifts
    .filter((s) => {
      if (s.status !== 'open') return false;
      const dt = s.date ? parseISO(s.date) : null;
      if (!dt || !isValid(dt)) return false;
      const diff = dt.getTime() - now;
      return diff > 0 && diff < oneDayMs;
    })
    .slice(0, 2)
    .forEach((s) => {
      needsAttention.push({
        key: `soon-${s.id}`,
        icon: AlertTriangle,
        tone: 'warning',
        message: `Tomorrow's shift at ${s.branchName} still needs cover`,
        actionLabel: 'Find replacement',
        onClick: () => navigate(`/manager/applicants?shiftId=${s.id}`),
      });
    });

  // 3. Completed shifts awaiting a rating
  shifts
    .filter((s) => s.status === 'completed' && s.assignedWorkerId && !s.ratingId)
    .slice(0, 2)
    .forEach((s) => {
      needsAttention.push({
        key: `rate-${s.id}`,
        icon: Star,
        tone: 'primary',
        message: `Rate ${s.assignedWorkerName || 'the worker'} for ${s.branchName}`,
        actionLabel: 'Leave a rating',
        onClick: () => navigate(`/manager/my-shifts?tab=history#shift-${s.id}`),
      });
    });

  // 4. Urgent till/discrepancy alerts (single roll-up entry — details live in Alerts)
  if ((stats?.urgentAlerts || 0) > 0) {
    needsAttention.push({
      key: 'urgent-alerts',
      icon: MessageSquareWarning,
      tone: 'danger',
      message: `${stats.urgentAlerts} till discrepancy alert${stats.urgentAlerts === 1 ? '' : 's'} need review`,
      actionLabel: 'View alerts',
      onClick: () => navigate('/manager/alerts'),
    });
  }

  // 5. Completed till-check awaiting manager review
  if ((stats?.needsReviewCount || 0) > 0) {
    needsAttention.push({
      key: 'needs-review',
      icon: ClipboardCheck,
      tone: 'primary',
      message: `${stats.needsReviewCount} till check${stats.needsReviewCount === 1 ? '' : 's'} awaiting your review`,
      actionLabel: 'Open shift records',
      onClick: () => navigate('/manager/my-shifts?tab=history'),
    });
  }

  const upcoming = shifts
    .filter((s) => s.status === 'open' || s.status === 'assigned' || s.status === 'in_progress')
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .slice(0, 4);

  // --- UI -------------------------------------------------------------------
  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="manager-dashboard-loading">
        <Skeleton className="h-32" />
        <Skeleton className="h-24" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6" data-testid="manager-dashboard">
      {/* Payment-lock banner — kept from previous version because it's a
          real blocker for the primary action. */}
      {user?.paymentLocked && (
        <Card
          className="border-0 shadow-sm bg-red-50"
          data-testid="dashboard-payment-locked-banner"
        >
          <CardContent className="p-5">
            <div className="flex items-start sm:items-center justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3">
                <div className="bg-red-500 p-2.5 rounded-xl shrink-0">
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-red-800">Account locked — payment failed</p>
                  <p className="text-sm text-red-600">
                    {user.paymentLockedReason || 'Your last shift payment could not be taken from your card. Update your card to keep posting shifts.'}
                  </p>
                </div>
              </div>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white rounded-full min-h-[48px]"
                onClick={handleUpdateCard}
                disabled={cardLoading}
                data-testid="dashboard-update-card-btn"
              >
                {cardLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Update card
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 1. Greeting + primary Find Cover CTA. Deliberately over-sized so
             no secondary tile can visually compete with it. */}
      <section data-testid="home-hero">
        <h1
          className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-1"
          data-testid="home-greeting"
        >
          {timeGreeting}, {firstName}
        </h1>
        <p className="text-base sm:text-lg text-slate-600 mb-5" data-testid="home-greeting-subtitle">
          {isFirstVisit
            ? "Your branch is ready. Let's get your first shift covered."
            : 'Welcome back.'}
        </p>
        <Button
          onClick={() => navigate('/manager/post-shift')}
          className="w-full sm:w-auto min-h-[56px] h-14 px-8 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white text-lg font-semibold shadow-md hover:shadow-lg transition-all"
          data-testid="home-find-cover-btn"
        >
          <CalendarPlus className="h-5 w-5 mr-2" />
          Find Cover
        </Button>
      </section>

      {/* 2. Needs attention — actionable items only, no vanity stats. */}
      <section data-testid="home-needs-attention">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-900">Needs attention</h2>
          {needsAttention.length > 0 && (
            <Badge className="bg-amber-100 text-amber-800" data-testid="needs-attention-count">
              {needsAttention.length}
            </Badge>
          )}
        </div>
        {needsAttention.length === 0 ? (
          <Card className="border-0 shadow-sm" data-testid="needs-attention-empty">
            <CardContent className="p-6 text-center">
              <p className="text-base text-slate-600">You&apos;re all caught up.</p>
              <p className="text-sm text-slate-400 mt-1">We&apos;ll flag anything that needs your attention here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2.5">
            {needsAttention.map((n) => {
              const Icon = n.icon;
              const toneAccent = {
                warning: 'bg-amber-50 border-amber-200',
                danger: 'bg-red-50 border-red-200',
                primary: 'bg-blue-50 border-blue-200',
                success: 'bg-emerald-50 border-emerald-200',
              }[n.tone] || 'bg-slate-50 border-slate-200';
              const iconTone = {
                warning: 'text-amber-600',
                danger: 'text-red-600',
                primary: 'text-blue-600',
                success: 'text-emerald-600',
              }[n.tone] || 'text-slate-500';
              return (
                <button
                  key={n.key}
                  onClick={n.onClick}
                  className={`w-full text-left rounded-xl border ${toneAccent} p-4 flex items-center gap-3 transition-all hover:shadow-sm active:scale-[0.99] min-h-[64px]`}
                  data-testid={`needs-attention-${n.key}`}
                >
                  <Icon className={`h-5 w-5 shrink-0 ${iconTone}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-base text-slate-800 font-medium leading-snug">{n.message}</p>
                    <p className="text-sm text-slate-500 mt-0.5 inline-flex items-center gap-0.5">
                      {n.actionLabel} <ChevronRight className="h-3.5 w-3.5" />
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* 3. Upcoming shifts — next few cards with ONE primary action each. */}
      <section data-testid="home-upcoming-shifts">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-900">Upcoming shifts</h2>
          {upcoming.length > 0 && (
            <button
              onClick={() => navigate('/manager/my-shifts')}
              className="text-sm text-[#DA291C] font-medium hover:underline"
              data-testid="upcoming-see-all-btn"
            >
              See all
            </button>
          )}
        </div>
        {upcoming.length === 0 ? (
          <Card className="border-0 shadow-sm" data-testid="upcoming-empty">
            <CardContent className="p-8 text-center">
              <p className="text-base text-slate-800 font-medium mb-1">You don&apos;t have any upcoming shifts.</p>
              <Button
                onClick={() => navigate('/manager/post-shift')}
                className="mt-3 rounded-full min-h-[48px] bg-[#DA291C] hover:bg-[#b91f13] text-white"
                data-testid="upcoming-empty-cta"
              >
                <CalendarPlus className="h-4 w-4 mr-1.5" />
                Find Cover
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {upcoming.map((shift) => {
              const status = getShiftStatus(shift);
              // ONE primary action per card. Precedence:
              //   1. Applicants waiting → Review workers
              //   2. Assigned worker    → View shift (log)
              //   3. Open, no apps      → Find replacement / Cancel
              let primary = { label: 'View shift', onClick: () => navigate(`/manager/logs/${shift.id}`) };
              if (shift.status === 'open' && (shift.applicantCount || 0) > 0) {
                primary = { label: `Review workers (${shift.applicantCount})`, onClick: () => navigate(`/manager/applicants?shiftId=${shift.id}`) };
              } else if (shift.status === 'assigned') {
                primary = { label: 'View shift', onClick: () => navigate(`/manager/logs/${shift.id}`) };
              } else if (shift.status === 'in_progress') {
                primary = { label: 'View shift', onClick: () => navigate(`/manager/logs/${shift.id}`) };
              } else if (shift.status === 'open') {
                primary = { label: 'Find replacement', onClick: () => navigate(`/manager/applicants?shiftId=${shift.id}`) };
              }
              return (
                <Card
                  key={shift.id}
                  className="border-0 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`upcoming-shift-${shift.id}`}
                >
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-500">
                          {shift.date ? format(parseISO(shift.date), 'EEE d MMM') : ''}
                        </p>
                        <p className="text-base font-semibold text-slate-900 truncate">{shift.branchName}</p>
                      </div>
                      <Badge className={statusToneClasses(status.tone)} data-testid={`upcoming-shift-status-${shift.id}`}>
                        {status.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{shift.scheduledStartTime}–{shift.scheduledEndTime}</span>
                      <span className="inline-flex items-center gap-1"><Banknote className="h-4 w-4" />£{Number(shift.pay || 0).toFixed(2)}</span>
                      {shift.location && (
                        <span className="inline-flex items-center gap-1 min-w-0"><MapPin className="h-4 w-4 shrink-0" /><span className="truncate">{shift.location}</span></span>
                      )}
                    </div>
                    <Button
                      onClick={primary.onClick}
                      variant="outline"
                      className="w-full sm:w-auto min-h-[48px] rounded-full border-slate-300 hover:bg-slate-50 text-slate-800 font-medium"
                      data-testid={`upcoming-shift-primary-${shift.id}`}
                    >
                      {primary.label} <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
